import React, { useState, useEffect } from 'react';
import './exportmodal.css';
import { getHealthWorkersExport, getQuizzesExport, getCourses, getActivityLogs, getFacilities, getStates, getLgasByState } from "../api";

const userColumns = [
  { key: 'firstname', label: 'First Name' },
  { key: 'lastname', label: 'Last Name' },
  { key: 'phone', label: 'Phone Number' },
  { key: 'dob', label: 'Date of Birth' },
  { key: 'createdAt', label: 'Created At' },
  { key: 'age', label: 'Age' },
  { key: 'gender', label: 'Gender' },
  { key: 'cadre', label: 'Cadre' },
  { key: 'facility', label: 'Facility' },
  { key: 'lga', label: 'Local Government' },
  { key: 'courseEnrolled', label: 'Courses Enrolled' },
  { key: 'courseCompleted', label: 'Courses Completed' },
  { key: 'averageScore', label: 'Average Score' },
  { key: 'employment_type', label: 'Employment Type' },
  { key: 'highest_qualification', label: 'Highest Qualification'},
  { key: 'status', label: 'Registration Status' },
  { key: 'enrolledCourses', label: 'Enrolled Courses List' },
  { key: 'completedCourses', label: 'Completed Courses List' },
  { key: 'enrollmentDates', label: 'Enrollment Dates' },
  { key: 'completionDates', label: 'Completion Dates' },
  { key: 'lastLoginDate', label: 'Last Login Date' },
  { key: 'totalEnrollments', label: 'Total Enrollments' },
  { key: 'activeEnrollments', label: 'Active Enrollments' },
  { key: 'recentActivity', label: 'Recent Activity Summary' },
];

const courseColumns = [
  { key: 'name', label: 'Course Name' },
  { key: 'description', label: 'Description' },
  { key: 'category', label: 'Category' },
  { key: 'enrollees', label: 'Enrollees' },
  { key: 'completed', label: 'Completed' },
  { key: 'active_learners', label: 'Pending' },
  { key: 'tests_avg_percentile', label: 'Average Score (Post)' },
  { key: 'drop_off_rate', label: 'Drop-off Rate (%) - 1 Month' },
];

const quizColumns = [
  { key: 'course_name', label: 'Course Name' },
  { key: 'student_name', label: 'Student Name' },
  { key: 'student_phone', label: 'Student Phone' },
  { key: 'pre_test_score', label: 'Pre-Test Score' },
  { key: 'pre_test_percentile', label: 'Pre-Test Percentile (%)' },
  { key: 'post_test_score', label: 'Post-Test Score' },
  { key: 'post_test_percentile', label: 'Post-Test Percentile (%)' },
  { key: 'percentage_gained', label: 'Percentage Gained (%)' },
];

const activityLogColumns = [
  { key: 'id', label: 'Log ID' },
  { key: 'userName', label: 'User Name' },
  { key: 'userEmail', label: 'User Email' },
  { key: 'userPhone', label: 'User Phone' },
  { key: 'userType', label: 'User Type' },
  { key: 'logName', label: 'Activity Type' },
  { key: 'description', label: 'Description' },
  { key: 'timestamp', label: 'Timestamp' },
  { key: 'subjectType', label: 'Subject Type' },
  { key: 'subjectId', label: 'Subject ID' },
];

const enrollmentColumns = [
  { key: 'learnerName', label: 'Learner Name' },
  { key: 'learnerEmail', label: 'Learner Email' },
  { key: 'learnerPhone', label: 'Learner Phone' },
  { key: 'facility', label: 'Facility' },
  { key: 'lga', label: 'LGA' },
  { key: 'courseName', label: 'Course Name' },
  { key: 'status', label: 'Enrollment Status' },
  { key: 'enrolledDate', label: 'Enrolled Date' },
  { key: 'completedDate', label: 'Completed Date' },
  { key: 'progress', label: 'Progress' },
];

const dataTypes = [
  { id: 'users', label: 'Users', icon: '👥', sub: ['All Users', 'Health Workers', 'Admins'] },
  { id: 'courses', label: 'Courses', icon: '📚', sub: ['All Courses', 'Active Courses', 'Archived Courses'] },
  { id: 'quizzes', label: 'Quizzes', icon: '❓', sub: ['All Quizzes', 'Published Quizzes', 'Draft Quizzes'] },
];

const ExportModal = ({ isOpen, onClose, onExport, context }) => {
  const [activeMode, setActiveMode] = useState(context?.type ? 'page' : 'general');
  const [selectedDataType, setSelectedDataType] = useState(null); // Changed to single selection
  const [subOptions, setSubOptions] = useState({});
  const [quizCourseOptions, setQuizCourseOptions] = useState([]);
  const [loadingQuizCourses, setLoadingQuizCourses] = useState(false);
  const [selectedQuizCourseId, setSelectedQuizCourseId] = useState('');
  const [lgaOptions, setLgaOptions] = useState([]);
  const [loadingLgas, setLoadingLgas] = useState(false);
  const [selectedLgaId, setSelectedLgaId] = useState('');
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [loadingFacilities, setLoadingFacilities] = useState(false);
  const [selectedFacilityId, setSelectedFacilityId] = useState('');
  const [selectedColumns, setSelectedColumns] = useState(userColumns);
  const [format, setFormat] = useState('csv');
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [recentExports, setRecentExports] = useState([]);

  useEffect(() => {
    if (context?.type === 'users' || context?.type === 'activity-logs' || context?.type === 'enrollments') {
      setActiveMode('page');
      // Set appropriate columns based on context
      if (context?.type === 'activity-logs') {
        setSelectedColumns(activityLogColumns);
      } else if (context?.type === 'enrollments') {
        setSelectedColumns(enrollmentColumns);
      }
    } else {
      setActiveMode('general');
    }
  }, [context]);

  useEffect(() => {
    if (selectedDataType !== 'quizzes') {
      setSelectedQuizCourseId('');
      return;
    }

    let cancelled = false;
    const fetchQuizCourses = async () => {
      setLoadingQuizCourses(true);
      try {
        const courses = await getCourses();
        const mappedCourses = (Array.isArray(courses) ? courses : [])
          .map((course) => ({
            id: course.id,
            name: course.title || course.name || `Course ${course.id}`,
          }))
          .filter((course) => course.id)
          .sort((a, b) => a.name.localeCompare(b.name));

        if (!cancelled) {
          setQuizCourseOptions(mappedCourses);
        }
      } catch (err) {
        console.error('Failed to fetch courses for quiz filter:', err);
        if (!cancelled) {
          setQuizCourseOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingQuizCourses(false);
        }
      }
    };

    fetchQuizCourses();

    return () => {
      cancelled = true;
    };
  }, [selectedDataType]);

  useEffect(() => {
    if (selectedDataType !== 'users') {
      setSelectedLgaId('');
      setSelectedFacilityId('');
      return;
    }

    let cancelled = false;
    const fetchLgaOptions = async () => {
      setLoadingLgas(true);
      try {
        const states = await getStates();
        const stateIds = (Array.isArray(states) ? states : [])
          .map((state) => state.id)
          .filter((id) => id != null);

        const lgasByState = await Promise.all(stateIds.map((stateId) => getLgasByState(stateId)));

        const uniqueLgas = [];
        const seenIds = new Set();
        lgasByState.flat().forEach((lga) => {
          const lgaId = lga.id ?? lga.lga_id ?? lga.lgaId;
          const lgaName = lga.name ?? lga.lga_name ?? lga.lgaName;
          if (lgaId && !seenIds.has(lgaId)) {
            seenIds.add(lgaId);
            uniqueLgas.push({ id: lgaId, name: lgaName || `LGA ${lgaId}` });
          }
        });
        uniqueLgas.sort((a, b) => a.name.localeCompare(b.name));

        if (!cancelled) {
          setLgaOptions(uniqueLgas);
        }
      } catch (err) {
        console.error('Failed to fetch LGAs for export filter:', err);
        if (!cancelled) {
          setLgaOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingLgas(false);
        }
      }
    };

    fetchLgaOptions();

    return () => {
      cancelled = true;
    };
  }, [selectedDataType]);

  useEffect(() => {
    setSelectedFacilityId('');

    if (!selectedLgaId) {
      setFacilityOptions([]);
      return;
    }

    let cancelled = false;
    const fetchFacilityOptions = async () => {
      setLoadingFacilities(true);
      try {
        const facilities = await getFacilities(selectedLgaId);
        const sorted = [...(Array.isArray(facilities) ? facilities : [])]
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

        if (!cancelled) {
          setFacilityOptions(sorted);
        }
      } catch (err) {
        console.error('Failed to fetch facilities for export filter:', err);
        if (!cancelled) {
          setFacilityOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingFacilities(false);
        }
      }
    };

    fetchFacilityOptions();

    return () => {
      cancelled = true;
    };
  }, [selectedLgaId]);

  const calculateAge = (dob) => {
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const handleDataTypeChange = (id) => {
    // Only allow one selection at a time
    if (selectedDataType === id) {
      setSelectedDataType(null); // Allow deselecting
      setSelectedQuizCourseId('');
      setSelectedColumns(userColumns); // Reset to default
    } else {
      setSelectedDataType(id);
      if (id !== 'quizzes') {
        setSelectedQuizCourseId('');
      }
      // Automatically set default columns for the selected type
      if (id === 'courses') setSelectedColumns(courseColumns);
      else if (id === 'quizzes') setSelectedColumns(quizColumns);
      else setSelectedColumns(userColumns);
    }
    setError('');
  };

  const handleSubOptionChange = (typeId, subId) => {
    setSubOptions(prev => ({ ...prev, [typeId]: subId }));
  };

  const handleColumnChange = (key) => {
    setSelectedColumns(prev =>
      prev.find(col => col.key === key)
        ? prev.filter(col => col.key !== key)
        : [...prev, [...userColumns, ...courseColumns, ...quizColumns].find(col => col.key === key)]
    );
    setError('');
  };

  const handleModeChange = (mode) => {
    setActiveMode(mode);
    setError('');
    setSelectedDataType(null); // Reset to null for single selection
    setSubOptions({});
    setSelectedQuizCourseId('');

    // Reset columns to default based on mode
    if (mode === 'general') setSelectedColumns(userColumns);
    else if (context?.type === 'courses') setSelectedColumns(courseColumns);
    else if (context?.type === 'quizzes') setSelectedColumns(quizColumns);
    else setSelectedColumns(userColumns);
  };

  const fetchAllActivityLogs = async () => {
    try {
      let allLogs = [];
      let currentPage = 1;
      let hasMore = true;
      let totalPages = 1;
      
      console.log('Starting to fetch all activity logs...');
      
      while (hasMore) {
        const response = await getActivityLogs(currentPage, 100);
        console.log(`Activity logs page ${currentPage} response:`, response);
        
        // Handle different response structures - check Monitor.js pattern
        const logs = response.data || response.data?.data || [];
        const pagination = response.pagination || response.data?.pagination || {};
        
        console.log(`Page ${currentPage}: Found ${logs.length} logs`);
        allLogs = [...allLogs, ...logs];
        
        totalPages = pagination.last_page || response.last_page || response.data?.last_page || 1;
        hasMore = logs.length === 100 && currentPage < totalPages;
        currentPage++;
        
        // Update progress
        setProgress(Math.min(90, (currentPage / totalPages) * 80));
        
        // Safety limit
        if (currentPage > 100) {
          console.warn('Reached safety limit of 100 pages for activity logs');
          break;
        }
      }
      
      console.log(`Total activity logs fetched: ${allLogs.length}`);
      return allLogs;
    } catch (err) {
      console.error('Error fetching activity logs:', err);
      return [];
    }
  };

  const formatEnrollmentsForExport = (enrollments) => {
    if (!enrollments || enrollments.length === 0) return 'None';
    return enrollments
      .map(e => {
        const courseName = e.course || 'Unknown Course';
        const status = e.status ? ` (${e.status})` : '';
        return `${courseName}${status}`;
      })
      .join('; ');
  };

  const formatDatesForExport = (enrollments, type = 'enrolled') => {
    if (!enrollments || enrollments.length === 0) return 'N/A';
    const dates = enrollments
      .map(e => {
        if (type === 'enrolled') {
          const date = e.created_at || e.createdAt;
          return date ? new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '';
        }
        if (type === 'completed') {
          const date = e.completed_at || (e.status === 'completed' ? (e.updated_at || e.updatedAt) : null);
          return date ? new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '';
        }
        return '';
      })
      .filter(d => d);
    return dates.length > 0 ? dates.join('; ') : 'N/A';
  };

  // Helper function to sanitize text and remove invalid characters
  const sanitizeText = (text) => {
    if (!text && text !== 0) return '';
    return String(text)
      .replace(/[\u0000-\u001F\u007F-\u009F]/g, '') // Remove control characters
      .replace(/[\u200B-\u200D\uFEFF]/g, '') // Remove zero-width characters
      .replace(/\n/g, ' ') // Replace newlines with spaces
      .replace(/\r/g, '') // Remove carriage returns
      .replace(/\t/g, ' ') // Replace tabs with spaces
      .replace(/[^\x20-\x7E\u00A0-\uFFFF]/g, '') // Keep only printable characters and extended unicode
      .trim();
  };

  // Helper function to format date safely
  const formatDateSafe = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch (e) {
      return 'N/A';
    }
  };

  const normalizeSync = (items, type) => {
    return items.map(item => {
      // Enrollments and activity data are now embedded directly by the export endpoint.
      const userEnrollments = Array.isArray(item.enrolments) ? item.enrolments : [];
      const completedEnrollments = userEnrollments.filter(e => e.status === 'completed' || e.status === 'Completed');
      const activeEnrollments = userEnrollments.filter(e => e.status !== 'completed' && e.status !== 'Completed');
      
      return selectedColumns.reduce((acc, col) => {
        switch (type) {
          case 'users':
          case 'learners':
          case 'staff':
            switch (col.key) {
              case 'firstname': 
                acc[col.label] = sanitizeText(item.first_name || item.firstname || ''); 
                break;
              case 'lastname': 
                acc[col.label] = sanitizeText(item.last_name || item.lastname || ''); 
                break;
              case 'phone': 
                acc[col.label] = sanitizeText(item.phone_number || item.phone || ''); 
                break;
              case 'dob': 
                acc[col.label] = item.dob ? formatDateSafe(item.dob) : ''; 
                break;
              case 'age':
                acc[col.label] = item.age ? String(item.age) : (item.dob ? String(calculateAge(item.dob)) : '');
                break;
              case 'createdAt':
                acc[col.label] = item.created_at ? formatDateSafe(item.created_at) : 'N/A';
                break;
              case 'gender':
                acc[col.label] = sanitizeText(item.gender || item.sex || 'N/A');
                break;
              case 'cadre': 
                acc[col.label] = sanitizeText(item.cadre?.name || item.job_title?.title || item.job_title_name || 'N/A'); 
                break;
              case 'facility': 
                acc[col.label] = sanitizeText(item.facility?.name || item.facility_name || 'N/A'); 
                break;
              case 'lga': 
                acc[col.label] = sanitizeText(item.lga?.name || item.local_government || item.lga_name || 'N/A'); 
                break;
              case 'courseEnrolled': 
                acc[col.label] = String(item.course_enrolled || item.enrolled_courses_count || userEnrollments.length || 0); 
                break;
              case 'courseCompleted': 
                acc[col.label] = String(item.course_completed || item.completed_courses_count || completedEnrollments.length || 0); 
                break;
              case 'averageScore': 
                acc[col.label] = item.average_score != null ? String(item.average_score) : (item.tests_avg_percentile != null ? String(item.tests_avg_percentile) : ''); 
                break;
              case 'employment_type':
                acc[col.label] = sanitizeText(item.employment_type || item.employmentType || item.type_of_employment || 'N/A');
                break;
              case 'status': 
                acc[col.label] = sanitizeText(item.status || (item.is_active ? 'Active' : 'Inactive')); 
                break;
              case 'enrolledCourses': 
                acc[col.label] = sanitizeText(formatEnrollmentsForExport(userEnrollments)); 
                break;
              case 'completedCourses': 
                acc[col.label] = sanitizeText(formatEnrollmentsForExport(completedEnrollments)); 
                break;
              case 'enrollmentDates': 
                acc[col.label] = sanitizeText(formatDatesForExport(userEnrollments, 'enrolled')); 
                break;
              case 'completionDates': 
                acc[col.label] = sanitizeText(formatDatesForExport(completedEnrollments, 'completed')); 
                break;
              case 'lastLoginDate': {
                const lastLoginAt = item.last_login_at;
                acc[col.label] = lastLoginAt ? formatDateSafe(lastLoginAt) : 'N/A';
                break;
              }
              case 'totalEnrollments': 
                acc[col.label] = String(userEnrollments.length || 0); 
                break;
              case 'activeEnrollments': 
                acc[col.label] = String(activeEnrollments.length || 0); 
                break;
              case 'recentActivity': {
                const activities = Array.isArray(item.recent_activities) ? item.recent_activities : [];
                if (activities.length === 0) {
                  acc[col.label] = 'No recent activity';
                } else {
                  acc[col.label] = sanitizeText(
                    activities.map(a => {
                      const action = a.log_name || a.description || 'Activity';
                      const date = a.created_at
                        ? new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                        : '';
                      return `${action}${date ? ` (${date})` : ''}`;
                    }).join('; ')
                  );
                }
                break;
              }
              default: 
                acc[col.label] = item[col.key] != null ? sanitizeText(String(item[col.key])) : ''; 
                break;
            }
            break;

          case 'courses':
            switch (col.key) {
              case 'name': 
                acc[col.label] = sanitizeText(item.title || item.name || ''); 
                break;
              case 'description': 
                acc[col.label] = sanitizeText(item.description || ''); 
                break;
              case 'category': 
                acc[col.label] = sanitizeText(item.category?.name || item.category_name || item.category || ''); 
                break;
              case 'enrollees': 
                acc[col.label] = String(item.enrollees || item.learners_count || item.enrolled_count || 0); 
                break;
              case 'completed': 
                acc[col.label] = String(item.completed || item.completed_count || 0); 
                break;
              case 'pending': 
                acc[col.label] = String(item.pending || item.pending_count || 0); 
                break;
              case 'averageScore': 
                acc[col.label] = item.average_score != null ? String(item.average_score) : ''; 
                break;
              case 'drop_off_rate':
                acc[col.label] = item.drop_off_rate != null ? `${Number(item.drop_off_rate).toFixed(2)}%` : '0.00%';
                break;
              default: 
                acc[col.label] = item[col.key] != null ? sanitizeText(String(item[col.key])) : ''; 
                break;
            }
            break;

          case 'quizzes':
            switch (col.key) {
              case 'course_name':
                acc[col.label] = sanitizeText(item.course_name || '');
                break;
              case 'student_name':
                acc[col.label] = sanitizeText(item.student_name || '');
                break;
              case 'student_phone':
                acc[col.label] = sanitizeText(item.student_phone || 'N/A');
                break;
              case 'pre_test_score':
                acc[col.label] = item.pre_test_score != null ? String(item.pre_test_score) : 'N/A';
                break;
              case 'pre_test_percentile':
                acc[col.label] = item.pre_test_percentile != null ? String(item.pre_test_percentile) : 'N/A';
                break;
              case 'post_test_score':
                acc[col.label] = item.post_test_score != null ? String(item.post_test_score) : 'N/A';
                break;
              case 'post_test_percentile':
                acc[col.label] = item.post_test_percentile != null ? String(item.post_test_percentile) : 'N/A';
                break;
              case 'percentage_gained':
                acc[col.label] = item.percentage_gained != null ? String(item.percentage_gained) + '%' : 'N/A';
                break;
              default:
                acc[col.label] = item[col.key] != null ? sanitizeText(String(item[col.key])) : '';
                break;
            }
            break;

          default:
            acc[col.label] = item[col.key] ?? '';
        }
        return acc;
      }, {});
    });
  };

  /**
   * Fetch enriched health-worker data from the single export endpoint.
   * The backend joins enrolments and activity logs server-side, removing the
   * N+1 fan-out. When ids are provided only those users are fetched.
   */
  const fetchHealthWorkersExportData = async (ids = [], extraFilters = {}) => {
    setProgress(20);
    const filters = { ...extraFilters };
    if (ids.length > 0) filters.ids = ids;
    const res = await getHealthWorkersExport(filters);
    setProgress(70);
    return res;
  };

  const handleSubmit = async () => {
    if (activeMode === 'general' && !selectedDataType) {
      setError('Please select a data type.');
      return;
    }
    if (activeMode === 'page' && selectedColumns.length === 0) {
      setError('Please select at least one column to export.');
      return;
    }

    setExporting(true);
    setError('');
    setProgress(0);

    try {
      let exportData = [];
      let exportMetadata = {
        exportDate: new Date().toISOString(),
        exportType: activeMode === 'general' ? 'General Export' : 'Page Export',
        dataType: activeMode === 'general' ? selectedDataType : (context?.type || 'users'),
        format: format,
        totalRecords: 0,
        generatedBy: 'CareAdmin System',
        version: '1.0'
      };

      setProgress(10);

      // Handle special context types (activity-logs, enrollments, etc.)
      if (context?.type === 'activity-logs') {
        setProgress(10);
        let allLogs = [];
        
        // Fetch all logs if not filtered, or use provided logs if filtered
        if (context.filteredUserId) {
          // For filtered user, fetch all their logs
          try {
            allLogs = await fetchAllActivityLogs();
            allLogs = allLogs.filter(log => {
              const causerId = log.causer_id || log.causerId || log.causer?.id;
              return causerId === context.filteredUserId || causerId === parseInt(context.filteredUserId);
            });
          } catch (err) {
            console.error('Error fetching filtered logs:', err);
            allLogs = context.logs || [];
          }
        } else {
          // Fetch all logs
          try {
            allLogs = await fetchAllActivityLogs();
          } catch (err) {
            console.error('Error fetching all logs:', err);
            allLogs = context.logs || [];
          }
        }
        
        exportData = allLogs.map(log => {
          const causer = log.causer || {};
          const userName = causer.full_name || 
                          `${causer.firstname || ''} ${causer.lastname || ''}`.trim() || 
                          causer.email || 
                          causer.phone_number || 
                          'Unknown User';
          
          return selectedColumns.reduce((acc, col) => {
            let value = '';
            switch (col.key) {
              case 'id': 
                value = log.id ? String(log.id) : 'N/A'; 
                break;
              case 'userName': 
                value = sanitizeText(userName); 
                break;
              case 'userEmail': 
                value = sanitizeText(causer.email || 'N/A'); 
                break;
              case 'userPhone': 
                value = sanitizeText(causer.phone_number || 'N/A'); 
                break;
              case 'userType': 
                value = causer.is_admin === 1 || causer.is_admin === true ? 'ADMIN' : 'LEARNER'; 
                break;
              case 'logName': 
                value = sanitizeText(log.log_name || 'default'); 
                break;
              case 'description': 
                value = sanitizeText(log.description || 'N/A'); 
                break;
              case 'timestamp': 
                value = formatDateSafe(log.created_at); 
                break;
              case 'subjectType': 
                value = sanitizeText(log.subject_type || 'N/A'); 
                break;
              case 'subjectId': 
                value = log.subject_id ? String(log.subject_id) : 'N/A'; 
                break;
              default: 
                value = log[col.key] ? sanitizeText(String(log[col.key])) : ''; 
                break;
            }
            acc[col.label] = value;
            return acc;
          }, {});
        });
        exportMetadata.totalRecords = exportData.length;
        exportMetadata.dataType = 'activity-logs';
      } else if (context?.type === 'enrollments') {
        setProgress(10);
        const enrollments = context.enrollments || [];
        
        exportData = enrollments.map(enrollment => {
          // Handle different enrollment structures
          const learner = enrollment.staff || enrollment.learner || enrollment.user || {};
          const learnerName = learner.full_name || 
                            `${learner.firstname || ''} ${learner.lastname || ''}`.trim() || 
                            learner.name ||
                            'Unknown';
          
          return selectedColumns.reduce((acc, col) => {
            let value = '';
            switch (col.key) {
              case 'learnerName': 
                value = sanitizeText(learnerName); 
                break;
              case 'learnerEmail': 
                value = sanitizeText(learner.email || 'N/A'); 
                break;
              case 'learnerPhone': 
                value = sanitizeText(learner.phone_number || learner.phone || 'N/A'); 
                break;
              case 'facility': 
                value = sanitizeText(learner.facility?.name || learner.facility_name || 'N/A'); 
                break;
              case 'lga': 
                value = sanitizeText(learner.lga?.name || learner.lga_name || 'N/A'); 
                break;
              case 'courseName': 
                value = sanitizeText(context.courseName || enrollment.course?.title || enrollment.course?.name || 'N/A'); 
                break;
              case 'status': 
                value = sanitizeText(enrollment.status || 'active'); 
                break;
              case 'enrolledDate': 
                value = formatDateSafe(enrollment.created_at || enrollment.enrolled_at); 
                break;
              case 'completedDate': 
                value = (enrollment.status === 'completed' && enrollment.updated_at) 
                  ? formatDateSafe(enrollment.updated_at) 
                  : 'N/A'; 
                break;
              case 'progress': 
                value = enrollment.progress ? String(enrollment.progress) : '0'; 
                break;
              default: 
                value = enrollment[col.key] ? sanitizeText(String(enrollment[col.key])) : ''; 
                break;
            }
            acc[col.label] = value;
            return acc;
          }, {});
        });
        exportMetadata.totalRecords = exportData.length;
        exportMetadata.dataType = 'enrollments';
      } else if (activeMode === 'general') {
        if (selectedDataType === 'users') {
          setProgress(10);
          const res = await fetchHealthWorkersExportData([], {
            lga_id: selectedLgaId || undefined,
            facility_id: selectedFacilityId || undefined,
          });
          exportMetadata.totalRecords = res.length;
          exportData = normalizeSync(res, 'users');
        } else if (selectedDataType === 'courses') {
          setProgress(10);
          const res = await getCourses();
          exportData = normalizeSync(res, 'courses');
          exportMetadata.totalRecords = res.length;
        } else if (selectedDataType === 'quizzes') {
          setProgress(10);
          const res = await getQuizzesExport(selectedQuizCourseId || null);
          setProgress(70);
          exportData = normalizeSync(res, 'quizzes');
          exportMetadata.totalRecords = res.length;
        }
      } else {
        // Page-level export: fetch the same single endpoint filtered to the visible user IDs
        const pageUserIds = (context?.filteredUsers || []).map(u => u.id).filter(Boolean);
        setProgress(10);
        const res = await fetchHealthWorkersExportData(pageUserIds);
        exportMetadata.totalRecords = res.length;
        exportData = normalizeSync(res, context?.type || 'users');
      }

      setProgress(90);

      // === CSV Export ===
      if (format === 'csv') {
        const csvRows = [];
        
        // Professional metadata header
        csvRows.push('═══════════════════════════════════════════════════════════════');
        csvRows.push('                    DATA EXPORT REPORT');
        csvRows.push('═══════════════════════════════════════════════════════════════');
        csvRows.push('');
        csvRows.push('EXPORT INFORMATION');
        csvRows.push(`Generated By,${exportMetadata.generatedBy}`);
        csvRows.push(`Export Date,${new Date(exportMetadata.exportDate).toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'medium' })}`);
        csvRows.push(`Export Type,${exportMetadata.exportType}`);
        csvRows.push(`Data Type,${exportMetadata.dataType.charAt(0).toUpperCase() + exportMetadata.dataType.slice(1)}`);
        csvRows.push(`Total Records,${exportMetadata.totalRecords}`);
        csvRows.push(`Export Format,${exportMetadata.format.toUpperCase()}`);
        csvRows.push('');
        csvRows.push('═══════════════════════════════════════════════════════════════');
        csvRows.push('');
        
        // Main data with professional formatting
        if (exportData.length > 0) {
          csvRows.push('EXPORT DATA');
          csvRows.push('');
          const headers = selectedColumns.map(c => c.label);
          csvRows.push(headers.join(','));
          exportData.forEach((row, index) => {
            const values = headers.map(h => {
              const val = row[h];
              // Handle nested objects/arrays
              if (typeof val === 'object' && val !== null) {
                const jsonStr = JSON.stringify(val);
                return sanitizeText(jsonStr).replace(/"/g, '""');
              }
              // Escape commas and quotes for CSV, handle null/undefined, sanitize
              const stringVal = (val === null || val === undefined) ? '' : sanitizeText(String(val));
              // Escape quotes for CSV (double quotes for CSV escaping)
              return stringVal.replace(/"/g, '""');
            });
            csvRows.push(`"${values.join('","')}"`);
          });
          csvRows.push('');
          csvRows.push(`Total Records Exported: ${exportData.length}`);
        }
        
        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        const timestamp = new Date().toISOString().split('T')[0];
        const dataTypeName = exportMetadata.dataType.charAt(0).toUpperCase() + exportMetadata.dataType.slice(1);
        a.href = url;
        a.download = `CareAdmin_Export_${dataTypeName}_${timestamp}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }

      // === JSON Export ===
      if (format === 'json') {
        // Sanitize all data in exportData before JSON export
        const sanitizedExportData = exportData.map(row => {
          const sanitizedRow = {};
          Object.keys(row).forEach(key => {
            const value = row[key];
            if (typeof value === 'string') {
              sanitizedRow[key] = sanitizeText(value);
            } else if (value != null) {
              sanitizedRow[key] = value;
            } else {
              sanitizedRow[key] = '';
            }
          });
          return sanitizedRow;
        });

        const jsonData = {
          exportInfo: {
            generatedBy: exportMetadata.generatedBy,
            version: exportMetadata.version,
            exportDate: exportMetadata.exportDate,
            exportType: exportMetadata.exportType,
            dataType: exportMetadata.dataType,
            totalRecords: exportMetadata.totalRecords,
            format: exportMetadata.format
          },
          metadata: {
            exportTimestamp: new Date().toISOString(),
            exportDateFormatted: new Date(exportMetadata.exportDate).toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'medium' }),
            recordCount: sanitizedExportData.length,
            columns: selectedColumns.map(c => ({ key: c.key, label: c.label }))
          },
          data: sanitizedExportData
        };
        
        const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        const timestamp = new Date().toISOString().split('T')[0];
        const dataTypeName = exportMetadata.dataType.charAt(0).toUpperCase() + exportMetadata.dataType.slice(1);
        a.href = url;
        a.download = `CareAdmin_Export_${dataTypeName}_${timestamp}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      }

      // === PDF Export ===
      if (format === 'pdf') {
        const { jsPDF } = await import('jspdf');
        const doc = new jsPDF();
        let yPos = 20;
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 15;
        const maxWidth = pageWidth - (margin * 2);
        
        // Helper function to add text with word wrap
        const addText = (text, x, y, maxWidth, fontSize = 10) => {
          doc.setFontSize(fontSize);
          const sanitizedText = sanitizeText(String(text || ''));
          const lines = doc.splitTextToSize(sanitizedText, maxWidth);
          doc.text(lines, x, y);
          return lines.length * (fontSize * 0.4);
        };
        
        // Professional Header
        doc.setFillColor(247, 118, 0);
        doc.rect(0, 0, pageWidth, 35, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(20);
        doc.setFont(undefined, 'bold');
        doc.text('CareAdmin System', margin, 18);
        doc.setFontSize(16);
        doc.text('Data Export Report', margin, 28);
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.text(`Generated: ${new Date().toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'medium' })}`, margin, 33);
        yPos = 45;
        
        // Professional Metadata section
        doc.setTextColor(0, 0, 0);
        doc.setFillColor(245, 245, 245);
        doc.roundedRect(margin, yPos, maxWidth, 35, 3, 3, 'F');
        yPos += 8;
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(247, 118, 0);
        doc.text('Export Information', margin + 5, yPos);
        yPos += 8;
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(60, 60, 60);
        yPos += addText(`Generated By: ${exportMetadata.generatedBy}`, margin + 5, yPos, maxWidth - 10, 9);
        yPos += addText(`Export Type: ${exportMetadata.exportType}`, margin + 5, yPos, maxWidth - 10, 9);
        yPos += addText(`Data Type: ${exportMetadata.dataType.charAt(0).toUpperCase() + exportMetadata.dataType.slice(1)}`, margin + 5, yPos, maxWidth - 10, 9);
        yPos += addText(`Total Records: ${exportMetadata.totalRecords}`, margin + 5, yPos, maxWidth - 10, 9);
        yPos += addText(`Export Date: ${new Date(exportMetadata.exportDate).toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'medium' })}`, margin + 5, yPos, maxWidth - 10, 9);
        yPos += 10;
        
        // Professional Main data section
        if (exportData.length > 0) {
          if (yPos > 250) {
            doc.addPage();
            yPos = 20;
          }
          doc.setFontSize(14);
          doc.setFont(undefined, 'bold');
          doc.setTextColor(247, 118, 0);
          doc.text('Export Data', margin, yPos);
          yPos += 8;
          
          doc.setFontSize(8);
          doc.setFont(undefined, 'normal');
          doc.setTextColor(100, 100, 100);
          doc.text(`Showing ${Math.min(20, exportData.length)} of ${exportData.length} records`, margin, yPos);
          yPos += 10;
          
          const headers = selectedColumns.map(c => c.label);
          
          // Display data in a professional table-like format
          exportData.slice(0, 20).forEach((row, idx) => {
            if (yPos > 260) {
              doc.addPage();
              yPos = 20;
            }
            
            // Record header with background
            doc.setFillColor(247, 118, 0);
            doc.roundedRect(margin, yPos - 4, maxWidth, 6, 2, 2, 'F');
            doc.setFontSize(9);
            doc.setFont(undefined, 'bold');
            doc.setTextColor(255, 255, 255);
            yPos += addText(`Record ${idx + 1} of ${exportData.length}`, margin + 3, yPos, maxWidth - 6, 9);
            yPos += 3;
            
            // Record data with alternating background
            doc.setFontSize(7);
            doc.setFont(undefined, 'normal');
            doc.setTextColor(0, 0, 0);
            let fieldCount = 0;
            headers.forEach(header => {
              const rawValue = row[header] || 'N/A';
              const value = sanitizeText(String(rawValue));
              // Truncate long values for PDF
              const displayValue = value.length > 60 ? value.substring(0, 60) + '...' : value;
              
              // Alternate background for readability
              if (fieldCount % 2 === 0) {
                doc.setFillColor(250, 250, 250);
                doc.roundedRect(margin, yPos - 3, maxWidth, 4, 1, 1, 'F');
              }
              
              doc.setTextColor(60, 60, 60);
              yPos += addText(`${header}:`, margin + 5, yPos, maxWidth - 10, 7);
              doc.setTextColor(0, 0, 0);
              yPos += addText(displayValue, margin + 15, yPos, maxWidth - 20, 7);
              yPos += 2;
              fieldCount++;
            });
            yPos += 5;
          });
          
          if (exportData.length > 20) {
            yPos += 5;
            doc.setFontSize(8);
            doc.setFont(undefined, 'italic');
            doc.setTextColor(150, 150, 150);
            doc.text(`Note: ${exportData.length - 20} additional records are available in the full export file.`, margin, yPos);
            doc.setTextColor(0, 0, 0);
            doc.setFont(undefined, 'normal');
          }
        }
        
        // Professional Footer on all pages
        const totalPages = doc.internal.pages.length - 1;
        for (let i = 1; i <= totalPages; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.setTextColor(150, 150, 150);
          const footerY = doc.internal.pageSize.getHeight() - 10;
          doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, footerY, { align: 'center' });
          doc.text(`CareAdmin System - Confidential`, margin, footerY);
          doc.text(`Generated: ${new Date().toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' })}`, pageWidth - margin, footerY, { align: 'right' });
        }
        
        const timestamp = new Date().toISOString().split('T')[0];
        const dataTypeName = exportMetadata.dataType.charAt(0).toUpperCase() + exportMetadata.dataType.slice(1);
        doc.save(`CareAdmin_Export_${dataTypeName}_${timestamp}.pdf`);
      }

      setProgress(100);
      
      // Show success message
      setTimeout(() => {
        setExporting(false);
        setProgress(0);
        onClose();
      }, 500);

    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export data. Check console for details.');
      setExporting(false);
      setProgress(0);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="export-modal-overlay">
      <div className="export-modal">
        {/* Modal Header */}
        <div className="modal-header">
          <h2>Export Center</h2>
          <button className="btn-close" onClick={onClose} aria-label="Close modal">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          {/* Mode Tabs */}
          <div className="mode-tabs">
            <button
              className={`mode-tab ${activeMode === 'general' ? 'active' : ''}`}
              onClick={() => handleModeChange('general')}
              aria-label="General export mode"
            >General Export</button>
            {context?.type && (
              <button
                className={`mode-tab ${activeMode === 'page' ? 'active' : ''}`}
                onClick={() => handleModeChange('page')}
                aria-label="Current page export mode"
              >Current Page</button>
            )}
          </div>

          <p className="modal-instructions">
            {activeMode === 'general'
              ? 'Select data types and customize your export for comprehensive data retrieval.'
              : `Export ${context?.activeTab === 'health-workers' ? 'health workers' : 'admins'} from the current page. Choose columns and format.`}
          </p>

          {activeMode === 'general' ? (
                        <div className="data-type-grid">
              {dataTypes.map(dt => (
                <div key={dt.id}>
                  <div
                    className={`data-type-card ${selectedDataType === dt.id ? 'selected' : ''}`}
                    onClick={() => handleDataTypeChange(dt.id)}
                    aria-label={`Select ${dt.label}`}
                    title={`Export ${dt.label}`}
                    role="radio"
                    aria-checked={selectedDataType === dt.id}
                  >
                    <span className="data-icon">{dt.icon}</span>
                    <span className="data-label">{dt.label}</span>
                    {selectedDataType === dt.id && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="selected-indicator">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                      </svg>
                    )}
                  </div>
                  {selectedDataType === dt.id && dt.id === 'quizzes' && (
                    <div className="sub-options">
                      <select
                        className="format-select"
                        value={selectedQuizCourseId}
                        onChange={(e) => setSelectedQuizCourseId(e.target.value)}
                        aria-label="Filter quizzes by course"
                      >
                        <option value="">{loadingQuizCourses ? 'Loading courses...' : 'All Quizzes'}</option>
                        {quizCourseOptions.map((course) => (
                          <option key={course.id} value={course.id}>
                            {course.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {selectedDataType === dt.id && dt.sub && dt.id !== 'quizzes' && (
                    <div className="sub-options">
                      {dt.sub.map((sub, index) => (
                        <div
                          key={index}
                          className={`sub-option-card ${subOptions[dt.id] === sub ? 'selected' : ''}`}
                          onClick={() => handleSubOptionChange(dt.id, sub)}
                          aria-label={`Select ${sub}`}
                        >
                          {sub}
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedDataType === dt.id && dt.id === 'users' && (
                    <div className="sub-options">
                      <select
                        className="format-select"
                        value={selectedLgaId}
                        onChange={(e) => setSelectedLgaId(e.target.value)}
                        aria-label="Filter health workers by Local Government"
                      >
                        <option value="">{loadingLgas ? 'Loading LGAs...' : 'All Local Governments'}</option>
                        {lgaOptions.map((lga) => (
                          <option key={lga.id} value={lga.id}>
                            {lga.name}
                          </option>
                        ))}
                      </select>
                      <select
                        className="format-select"
                        value={selectedFacilityId}
                        onChange={(e) => setSelectedFacilityId(e.target.value)}
                        aria-label="Filter health workers by Health Facility"
                        disabled={!selectedLgaId || loadingFacilities}
                        style={{ marginTop: '8px' }}
                      >
                        <option value="">
                          {!selectedLgaId ? 'Select a Local Government first' : loadingFacilities ? 'Loading facilities...' : 'All Facilities'}
                        </option>
                        {facilityOptions.map((facility) => (
                          <option key={facility.id} value={facility.id}>
                            {facility.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="summary-preview">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="summary-icon">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
                </svg>
                <span>
                  Exporting {context?.filteredUsers?.length || 0}{' '}
                  {context?.activeTab === 'health-workers' ? 'Health Workers' : 'Admins'}
                  {context?.searchTerm && ` (Search: "${context.searchTerm}")`}
                  {context?.sortBy && ` (Sorted by ${context.sortBy})`}
                </span>
              </div>
              <div className="column-selection">
                <h3>Select Columns</h3>
                <div className="column-grid">
                  {selectedColumns.map(col => (
                    <div
                      key={col.key}
                      className={`column-card ${selectedColumns.find(c => c.key === col.key) ? 'selected' : ''}`}
                      onClick={() => handleColumnChange(col.key)}
                      aria-label={`Select ${col.label} column`}
                    >
                      {col.label}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {error && (
            <div className="error-message">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" className="error-icon">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
              {error}
            </div>
          )}

          <div className="format-section">
            <label className="format-label">Export Format</label>
            <select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              className="format-select"
              aria-label="Select export format"
            >
              <option value="csv">CSV (Comma-Separated Values)</option>
              <option value="json">JSON (JavaScript Object Notation)</option>
              <option value="pdf">PDF (Portable Document Format)</option>
            </select>
          </div>

          {/* Additional Data Info */}
          {(activeMode === 'page' || selectedDataType === 'users') && (
            <div className="additional-data-section">
              <label className="format-label">Additional Data</label>
              <p className="additional-data-note" style={{ background: '#d1ecf1', borderColor: '#bee5eb', color: '#0c5460' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
                <span>Enrollments and Activity Logs will be automatically included in the export. Select the corresponding columns above to include them in your export.</span>
              </p>
            </div>
          )}

          {recentExports.length > 0 && (
            <div className="recent-exports">
              <h3>Recent Exports</h3>
              <ul>
                {recentExports.map(exp => (
                  <li key={exp.id} className="recent-export-item">
                    <span>{exp.name}</span>
                    <span>{exp.timestamp}</span>
                    <button
                      className="btn-re-download"
                      onClick={() => alert(`Re-download ${exp.name} (mock)`)}
                      aria-label={`Re-download ${exp.name}`}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 13v6H5v-6H3v8h18v-8h-2zm-6-10h-2v8.586L6.707 7.293 5.293 8.707 12 15.414l6.707-6.707-1.414-1.414L13 11.586V3z"/>
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {exporting && (
            <div className="progress-bar">
              <div className="progress" style={{ width: `${progress}%` }}></div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <button
            className="btn-cancel"
            onClick={onClose}
            disabled={exporting}
            aria-label="Cancel export"
          >
            Cancel
          </button>
          <button
            className="btn-export-modal"
            onClick={handleSubmit}
            disabled={exporting || (activeMode === 'general' ? !selectedDataType : selectedColumns.length === 0)}
            aria-label="Start export"
          >
            {exporting ? (
              <>
                <span className="spinner"></span> Exporting...
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="rocket-icon">
                  <path d="M12 2l-3 6H4l5 7-2 7 10-3 10 3-2-7 5-7h-5l-3-6-7 0zm0 2.25L14.5 8h-5L12 4.25zM4.8 9h3.4l-3.9 5.6.5-1.4 1.4-.5L4.8 9zm4.8 7.2l1.4.5.5 1.4-3.9-5.6 1.4 3.4 .6.3zM19.2 9l-1.4 3.4-.5 1.4 3.9-5.6H19.2zm-4.8 7.2l-1.4-.5-.5-1.4 3.9 5.6-3.4-1.4-.6-.3z"/>
                </svg>
                Launch Export
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;

